import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@finitless/shared/components";
import { Icon } from "../../ui/UIElements/Icon";
import PromotionCard from "../../ui/Discounts/PromotionCard";
import PromotionSkeletons from "../../ui/Discounts/PromotionSkeletons";
import CreatePromotionForm from "../../ui/Discounts/CreatePromotionForm";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import Pagination from "../../ui/Data/Pagination";
import DeleteConfirmationModal from "../../ui/Modals/DeleteConfirmationModal";
import { PROMO_STATUS } from "../../ui/Discounts/discountsConstants";
import {
  usePromotions,
  useTogglePromotionEnabled,
  useDeletePromotion,
} from "../../../hooks/usePromotions";
import { useToast, useUrlStates } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";

const mapApiPromotionToUiPromotion = (apiPromotion) => {
  if (!apiPromotion) {
    return null;
  }

  const getSafeDateString = (value) => {
    if (!value || typeof value !== "string") {
      return "";
    }
    // API format: "YYYY-MM-DD HH:mm:ss"
    return value.split(" ")[0] || value;
  };

  const now = new Date();
  const parseDate = (value) => {
    if (!value || typeof value !== "string") {
      return null;
    }
    // Replace space with 'T' for Date parsing compatibility
    const isoLike = value.replace(" ", "T");
    const date = new Date(isoLike);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const startDateObj = parseDate(apiPromotion.start);
  const endDateObj = parseDate(apiPromotion.end);

  let status = PROMO_STATUS.ACTIVE;
  if (!apiPromotion.enabled) {
    status = PROMO_STATUS.DISABLED;
  } else if (startDateObj && now < startDateObj) {
    status = PROMO_STATUS.SCHEDULED;
  } else if (endDateObj && now > endDateObj) {
    status = PROMO_STATUS.EXPIRED;
  }

  const rate_type = apiPromotion.rate_type;
  const target = apiPromotion.target;

  return {
    id: apiPromotion.id,
    name: apiPromotion.name,
    description: apiPromotion.description,
    coupon: apiPromotion.coupon,
    image: apiPromotion.image,
    rate: apiPromotion.rate,
    rate_type,
    target,
    stackable: Boolean(apiPromotion.stackable),
    start: getSafeDateString(apiPromotion.start),
    end: getSafeDateString(apiPromotion.end),
    status,
    enabled: apiPromotion.enabled,
    limit: apiPromotion.limit || 0,
    raw: apiPromotion,
  };
};

const DiscountsPage = ({
  initialPromotion = null,
  initialOpenRulesModal = false,
}) => {
  const { t } = useTranslation("discounts");
  const navigate = useNavigate();
  const { promotionId } = useParams();
  const toast = useToast();

  const [urlStates, updateUrlState] = useUrlStates({
    currentPage: "1",
  });

  const [promotions, setPromotions] = useState([]);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [viewMode, setViewMode] = useState(promotionId ? "create" : "list");
  const [editingPromotion, setEditingPromotion] = useState(initialPromotion);
  const [openRulesOnMount, setOpenRulesOnMount] = useState(
    Boolean(initialOpenRulesModal),
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(
    Number.parseInt(urlStates.currentPage, 10) || 1,
  );
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [promotionToDelete, setPromotionToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasLoadedPromotions, setHasLoadedPromotions] = useState(false);

  const ITEMS_PER_PAGE = 9;
  const MAX_VISIBLE_PAGES = 5;
  const togglePromotionEnabled = useTogglePromotionEnabled();
  const deletePromotionMutation = useDeletePromotion();

  const { data: promotionsData, isLoading: isLoadingPromotions } =
    usePromotions({
      page: currentPage,
      pageSize: ITEMS_PER_PAGE,
      searchTerm: debouncedSearchTerm,
      statusFilter: filterStatus,
    });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!promotionsData?.promotions) {
      return;
    }

    const mappedPromotions = promotionsData.promotions
      .map(mapApiPromotionToUiPromotion)
      .filter(Boolean);

    setPromotions(mappedPromotions);
    setHasLoadedPromotions(true);
  }, [promotionsData]);

  useEffect(() => {
    if (!initialOpenRulesModal) {
      return;
    }

    setOpenRulesOnMount(true);
  }, [initialOpenRulesModal]);

  useEffect(() => {
    if (!promotionId) {
      setViewMode("list");
      setEditingPromotion(null);
      setOpenRulesOnMount(false);
      return;
    }

    if (promotionId === "create") {
      setViewMode("create");
      setEditingPromotion(null);
      setOpenRulesOnMount(false);
      return;
    }

    const existingPromotion = promotions.find(
      (promotion) => promotion.id.toString() === promotionId.toString(),
    );

    if (existingPromotion) {
      setEditingPromotion(existingPromotion);
      setViewMode("create");
      return;
    }

    if (
      initialPromotion &&
      initialPromotion.id &&
      initialPromotion.id.toString() === promotionId.toString()
    ) {
      setEditingPromotion(initialPromotion);
      setViewMode("create");
      return;
    }

    setEditingPromotion(null);
    setViewMode("create");
  }, [promotionId, promotions, initialPromotion]);

  const filteredPromotions = useMemo(() => {
    return promotions.filter((promotion) => {
      if (filterStatus === "ACTIVE") {
        return (
          promotion.status === PROMO_STATUS.ACTIVE ||
          promotion.status === PROMO_STATUS.SCHEDULED ||
          promotion.status === PROMO_STATUS.EXPIRED
        );
      }

      if (filterStatus === "DISABLED") {
        return promotion.status === PROMO_STATUS.DISABLED;
      }

      return true;
    });
  }, [promotions, filterStatus]);

  const pagination = promotionsData?.pagination || {};
  const totalItems = pagination.total || filteredPromotions.length;
  const totalPages =
    pagination.total_pages ||
    Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  const showPromotionSkeletons = !hasLoadedPromotions || isLoadingPromotions;

  const handleChangeFilterStatus = (statusKey) => {
    setFilterStatus(statusKey);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleOpenCreatePromotion = () => {
    setEditingPromotion(null);
    navigate("/discounts/create");
  };

  const handleCloseCreatePromotion = () => {
    if (currentPage && currentPage > 1) {
      navigate(`/discounts?currentPage=${currentPage}`);
      return;
    }

    navigate("/discounts");
  };

  const handleCreateOrUpdatePromotion = (rawPromotion) => {
    if (!rawPromotion) {
      return;
    }

    const uiPromotion = mapApiPromotionToUiPromotion(rawPromotion);
    if (!uiPromotion) {
      return;
    }

    if (editingPromotion) {
      setPromotions((previousPromotions) =>
        previousPromotions.map((promotion) =>
          promotion.id.toString() === uiPromotion.id.toString()
            ? uiPromotion
            : promotion,
        ),
      );
      handleCloseCreatePromotion();
      return;
    }

    // Create new promotion from the actual API response
    setPromotions((previousPromotions) => [uiPromotion, ...previousPromotions]);
    handleCloseCreatePromotion();
  };

  const handleDeletePromotion = (promotionId) => {
    setPromotions((previousPromotions) =>
      previousPromotions.filter((promotion) => promotion.id !== promotionId),
    );
  };

  const handleTogglePromotionStatus = (promotionId) => {
    const targetPromotion = promotions.find(
      (promotion) => promotion.id === promotionId,
    );
    if (!targetPromotion) {
      return;
    }

    if (togglingIds.has(promotionId)) {
      return;
    }

    setTogglingIds((previous) => {
      const next = new Set(previous);
      next.add(promotionId);
      return next;
    });

    const currentEnabled =
      typeof targetPromotion.raw?.enabled === "boolean"
        ? targetPromotion.raw.enabled
        : targetPromotion.status !== PROMO_STATUS.DISABLED;

    const nextEnabled = !currentEnabled;

    // Optimistic UI update for server-backed promotions
    if (targetPromotion.raw) {
      setPromotions((previous) =>
        previous.map((promotion) =>
          promotion.id === promotionId
            ? mapApiPromotionToUiPromotion({
                ...promotion.raw,
                enabled: nextEnabled,
              })
            : promotion,
        ),
      );
    } else {
      // Fallback for local-only promotions (not yet synced with API)
      setPromotions((previous) =>
        previous.map((promotion) => {
          if (promotion.id !== promotionId) {
            return promotion;
          }

          const nextStatus = nextEnabled
            ? PROMO_STATUS.ACTIVE
            : PROMO_STATUS.DISABLED;
          return {
            ...promotion,
            status: nextStatus,
          };
        }),
      );
    }

    togglePromotionEnabled.mutate(
      { id: promotionId, enabled: nextEnabled },
      {
        onSuccess: () => {
          handleApiSuccess(
            t("toasts.toggleSuccess", {
              status: nextEnabled ? t("status.enabled") : t("status.disabled"),
            }),
            toast,
          );
        },
        onError: (error) => {
          handleApiError(error, toast, {
            title: nextEnabled
              ? t("toasts.toggleErrorEnableTitle")
              : t("toasts.toggleErrorDisableTitle"),
            defaultMessage: t("toasts.toggleErrorDefaultMessage"),
          });
        },
        onSettled: () => {
          setTogglingIds((previous) => {
            const next = new Set(previous);
            next.delete(promotionId);
            return next;
          });
        },
      },
    );
  };

  const handleEditPromotion = (promotion) => {
    setEditingPromotion(promotion);
    navigate(`/discounts/${promotion.id}`, { state: { promotion } });
  };

  const handleRequestDeletePromotion = (promotionId) => {
    const targetPromotion = promotions.find(
      (promotion) => promotion.id === promotionId,
    );
    if (!targetPromotion) {
      return;
    }
    setPromotionToDelete(targetPromotion);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDeletePromotion = async () => {
    if (!promotionToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      await deletePromotionMutation.mutateAsync({ id: promotionToDelete.id });
      handleDeletePromotion(promotionToDelete.id);
      handleApiSuccess(t("toasts.deleteSuccess"), toast);
      setIsDeleteModalOpen(false);
      setPromotionToDelete(null);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("toasts.deleteErrorTitle"),
        defaultMessage: t("toasts.deleteErrorDefaultMessage"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChangeSearchInput = (value) => {
    setSearchInput(value);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  // const activeCount = promotions.filter(
  //   (promotion) => promotion.status === PROMO_STATUS.ACTIVE,
  // ).length;

  const createOrEditBreadcrumbItems = useMemo(() => {
    if (viewMode !== "create") {
      return [];
    }

    return [
      {
        label: t("header.title"),
        path: "/discounts",
        icon: "Tag",
      },
      {
        label: editingPromotion
          ? t("breadcrumbs.editPromotion")
          : t("breadcrumbs.createPromotion"),
        disabled: true,
        active: true,
      },
    ];
  }, [viewMode, editingPromotion, t]);

  return (
    <div className="space-y-6">
      {viewMode === "create" && (
        <Breadcrumb items={createOrEditBreadcrumbItems} />
      )}

      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        {viewMode === "list" && (
          <>
            <PageHeader
              title={t("header.title")}
              subtitle={t("header.subtitle")}
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleOpenCreatePromotion}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-600/20 transition-all hover:bg-blue-500 hover:shadow-blue-600/30 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <Icon name="Plus" className="h-4 w-4" />
                <span>{t("actions.addPromotion")}</span>
              </button>
            </div>
          </>
        )}
      </div>

      {viewMode === "create" ? (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 sm:p-6">
          <CreatePromotionForm
            onClose={handleCloseCreatePromotion}
            onSubmit={handleCreateOrUpdatePromotion}
            initialData={editingPromotion}
            openRulesOnMount={openRulesOnMount}
            onConsumeOpenRulesOnMount={() => setOpenRulesOnMount(false)}
          />
        </div>
      ) : (
        <>
          <div className="mb-4 rounded-lg border border-gray-800 bg-gray-900 p-4 sm:mb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 flex-col gap-4 sm:flex-row">
                {/* Search */}
                <div className="min-w-0 flex-1">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Icon
                        name="Search"
                        className="h-4 w-4 text-gray-400 sm:h-5 sm:w-5"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder={t("search.placeholder")}
                      value={searchInput}
                      onChange={(event) =>
                        handleChangeSearchInput(event.target.value)
                      }
                      className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-sm text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={() => handleChangeSearchInput("")}
                        className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                      >
                        <svg
                          className="h-3 w-3 text-gray-300 hover:text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Status Filter */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <select
                      value={filterStatus}
                      onChange={(event) =>
                        handleChangeFilterStatus(event.target.value)
                      }
                      className="block h-10 w-full cursor-pointer appearance-none rounded-md border border-gray-700 bg-gray-800 px-3 py-2 pr-10 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      aria-label={t("filters.statusAria")}
                    >
                      <option value="ALL">{t("filters.allStatuses")}</option>
                      <option value="ACTIVE">{t("filters.enabled")}</option>
                      <option value="DISABLED">{t("filters.disabled")}</option>
                    </select>
                    <Icon
                      name="ChevronDown"
                      className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-gray-300"
                      size={18}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showPromotionSkeletons ? (
            <PromotionSkeletons />
          ) : filteredPromotions.length > 0 ? (
            <>
              <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {filteredPromotions.map((promotion) => (
                    <PromotionCard
                      key={promotion.id}
                      promotion={promotion}
                      onEdit={handleEditPromotion}
                      onDelete={handleRequestDeletePromotion}
                      onToggleStatus={handleTogglePromotionStatus}
                      isUpdating={togglingIds.has(promotion.id)}
                    />
                  ))}
                </div>
              </div>
              {totalPages > 1 && (
                <div className="mt-8">
                  <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                    <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                      {/* Pagination Info */}
                      <div className="text-sm text-gray-400">
                        {t("pagination.showing", {
                          from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                          to: Math.min(
                            currentPage * ITEMS_PER_PAGE,
                            totalItems || 0,
                          ),
                          total: totalItems || 0,
                        })}
                      </div>

                      {/* Pagination Controls */}
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        maxVisiblePages={MAX_VISIBLE_PAGES}
                        disabled={isLoadingPromotions}
                        className="mt-4 sm:mt-0"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-900">
                <Icon name="Tag" className="h-10 w-10 text-gray-500" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-300">
                {t("empty.title")}
              </h3>
              <p className="text-sm text-gray-400">{t("empty.subtitle")}</p>
            </div>
          )}
        </>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (isDeleting) return;
          setIsDeleteModalOpen(false);
          setPromotionToDelete(null);
        }}
        onConfirm={handleConfirmDeletePromotion}
        itemName={promotionToDelete?.name}
        itemType={t("deleteModal.itemType")}
        title={t("deleteModal.title")}
        subtitle={t("deleteModal.subtitle")}
        confirmButtonText={t("deleteModal.confirmButtonText")}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default DiscountsPage;
